import type { AdminCatalogEvent, CatalogEventKind } from '@steado/admin-contract/catalog-history';

/**
 * What an event is called, in History and on Catalog changes. A pushed batch
 * is named by its note's first sentence, the way a commit is named by its
 * subject line.
 */

/** What made a change, as History names it. */
export const SOURCE_LABELS: Readonly<Record<CatalogEventKind, string>> = {
  console: 'Console',
  request: 'Food request',
  batch: 'Pushed batch',
  revert: 'Revert',
  system: 'System',
};

/** A source as a person reads it: a link's host and path, or the citation as written. */
export function sourceTitle(source: string): { title: string; href: string | null } {
  try {
    const url = new URL(source);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return { title: source, href: null };
    const path = url.pathname === '/' ? '' : url.pathname;
    return { title: `${url.hostname.replace(/^www\./, '')}${path}`, href: url.toString() };
  } catch {
    return { title: source, href: null };
  }
}

export function firstSentence(note: string | null | undefined): string | null {
  if (note === null || note === undefined) return null;
  const line = note.trim().split(/\n/)[0] ?? '';
  const sentence = (line.split(/(?<=[.!?])\s/)[0] ?? line).replace(/[.!?]$/, '').trim();
  return sentence === '' ? null : sentence;
}

/**
 * A food request's note without the request's id. Notes written before
 * 2026-10-13 began "Food request <uuid>: fix of Kulfi"; the console links the
 * request instead of printing its id.
 */
export function withoutRequestId(note: string): string {
  return note.replace(/^Food request [0-9a-f-]{36}:/, 'Food request:');
}

/** "Food request: fix of Kulfi", from an approval's note; null for any other note. */
export function requestTitle(note: string | null | undefined): string | null {
  const sentence = firstSentence(note === null || note === undefined ? null : withoutRequestId(note));
  return sentence?.startsWith('Food request: ') === true ? sentence : null;
}

/** The food request an event approved, or whose approval it reverted. */
export function requestIdOf(event: AdminCatalogEvent | undefined, source: string): string | null {
  const own = /^food_request:([0-9a-f-]{36})$/.exec(source)?.[1];
  return own ?? event?.revertOf?.foodRequestId ?? null;
}

/** Who made it: their name, their console email, or System. */
export function actorName(actor: AdminCatalogEvent['actor']): string {
  if (actor === null) return 'System';
  return actor.name ?? actor.email ?? 'Someone';
}

export function eventTitle(input: {
  readonly kind: CatalogEventKind;
  readonly source: string;
  readonly event: AdminCatalogEvent | undefined;
  /** Lines it changed, for a revert's title. */
  readonly values: number;
  /** Set when the event added the food or exercise itself. */
  readonly added?: string | undefined;
}): string {
  const { kind, source, event, values, added } = input;
  switch (kind) {
    case 'console':
      return 'Edited in the console';
    case 'request':
      return requestTitle(event?.note) ?? 'Food request approved';
    case 'batch': {
      if (source === 'curation') return 'Applied from the curation files';
      const name = firstSentence(event?.batch?.note ?? null);
      return name === null ? 'Pushed batch' : `Batch: ${name}`;
    }
    case 'revert': {
      const target = event?.revertOf ?? null;
      const request = requestTitle(target?.batchNote);
      if (request !== null) {
        return target?.changeId === null
          ? `${request}, reverted`
          : `Reverted ${values === 1 ? 'one change' : `${String(values)} values`} from ${request}`;
      }
      const batchName = firstSentence(target?.batchNote ?? null);
      if (target !== null && target.changeId === null && batchName !== null)
        return `Batch ${batchName} reverted`;
      const what = values === 1 ? 'one change' : `${String(values)} values`;
      return batchName === null ? `Reverted ${what}` : `Reverted ${what} from the batch ${batchName}`;
    }
    case 'system':
      return added ?? 'Changed by a database update';
  }
}
